// BM-014: Manage Order bottom sheet.
//
// Lets the engineer change the branch and/or fulfillment method for an
// entire branch group at once. Replaces the per-branch Pickup/Delivery
// toggle that used to live inside the card (BM-004).
//
// Collection branch is user-selectable, filtered to branches that carry
// stock for ALL products in the group (AC4). Delivery branch is
// system-determined and shown read-only (AC5): sourced from the item's
// `deliverySourceBranchName` when the stock endpoint annotated a
// cross-branch delivery, else the group's own branch.
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { Check, MapPin, X } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '../../radix'
import { api } from '../../../services/api'
import { formatEarliestDate } from '../../../utils/datetime'
import { nearestBranchOptions } from '../../../utils/branchOptions'
import type { CartItem } from '../../../types'

export interface ManageOrderBranchGroup {
  supplierId: string | number
  branchId: string | number | null
  supplierName: string
  branchName: string
  distanceMiles: number | null
  items: CartItem[]
}

export interface ManageOrderApplyResult {
  newBranchId: string | null
  newMethod: 'pickup' | 'delivery'
}

export interface ManageOrderSheetProps {
  open: boolean
  branchGroup: ManageOrderBranchGroup
  currentMethod: 'pickup' | 'delivery'
  // Location for the live-stock probe. Passed from the cart's session
  // so alternatives are scored the same way ProductDetail scores them
  // (nearest 5 branches per raw product id).
  postcode?: string
  latitude?: number
  longitude?: number
  // Open with the branch list already expanded. Set when the sheet is
  // opened from the cart's far-branch "Change branch" link (DEV-51278), so
  // the engineer lands straight on the branches to pick from.
  showBranchListOnOpen?: boolean
  onClose: () => void
  onApply: (change: ManageOrderApplyResult) => Promise<void> | void
}

interface Alternative {
  branchId: string
  branchName: string
  distanceMiles: number | null
  earliestCollection: string | null
}

export function ManageOrderSheet({
  open,
  branchGroup,
  currentMethod,
  postcode,
  latitude,
  longitude,
  showBranchListOnOpen = false,
  onClose,
  onApply,
}: ManageOrderSheetProps) {
  const currentBranchIdStr =
    branchGroup.branchId != null ? String(branchGroup.branchId) : null
  const [method, setMethod] = useState<'pickup' | 'delivery'>(currentMethod)
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(currentBranchIdStr)
  const [showBranchList, setShowBranchList] = useState(showBranchListOnOpen)
  const [isApplying, setIsApplying] = useState(false)

  // Reset local state whenever the sheet is (re-)opened for a group. The
  // parent may mount the sheet with the same component instance across
  // groups, so we resync on every open rather than on mount.
  useEffect(() => {
    if (!open) return
    setMethod(currentMethod)
    setSelectedBranchId(currentBranchIdStr)
    setShowBranchList(showBranchListOnOpen)
  }, [open, currentBranchIdStr, currentMethod, showBranchListOnOpen])

  // Method eligibility -- computed after the alternatives query lands
  // because pickupEnabled also considers alternative branches, not
  // just the current one. See the block below.
  const groupSupportsPickup = branchGroup.items.every((item) => item.canCollect !== false)
  // DC-backed pickup counts too — Cart, ChangeItem and ProductDetail all
  // treat max(branch, DC) as the orderable pickup capacity (the LATER date
  // just replaces the branch date at render time). Without the DC fallback,
  // an item whose branch shelf is empty today but whose warehouse can cover
  // the qty (e.g. CPS 954320 at PORTSWOOD: 0 today, 7857 later) would flip
  // Manage Order's Pickup gate to disabled even though pickup is legitimate.
  //
  // Use the `pickupDcStock ?? dcStock` fallback (not an independent OR) —
  // the CPS adapter deliberately emits pickup_dc_stock = 0 when no later
  // collection option exists while dc_stock may still be positive (that
  // capacity backs delivery, not pickup). ORing dcStock separately would
  // wrongly enable pickup for such lines. Same pattern the alternatives
  // query and ChangeItem use.
  const everyItemHasPickupStock = branchGroup.items.every((item) => {
    // A backorderable line is available for a channel the branch serves when
    // that channel has an availability date.
    if (
      item.backorderAllowed === true &&
      item.canCollect !== false &&
      item.backorderCollectionDate != null
    ) return true
    const pickupOption = item.fulfillmentOptions?.find((opt) => opt.type === 'pickup')
    const dcBackedPickup = item.pickupDcStock ?? item.dcStock ?? 0
    return (
      (pickupOption?.stockQuantity ?? 0) >= item.quantity ||
      dcBackedPickup >= item.quantity
    )
  })

  const groupSupportsDelivery = branchGroup.items.every((item) => item.canDeliver !== false)
  // Same channel-specific fallback rule as pickup — delivery_dc_stock is the
  // authoritative delivery DC capacity for split adapters (CPS); dc_stock is
  // only a fallback for legacy adapters (Wolseley) that don't split. ORing
  // them independently would let a Wolseley-shaped dc_stock override an
  // explicit CPS delivery_dc_stock = 0.
  const everyItemHasDeliveryStock = branchGroup.items.every((item) => {
    if (
      item.backorderAllowed === true &&
      item.canDeliver !== false &&
      item.backorderDeliveryDate != null
    ) return true
    const deliveryOption = item.fulfillmentOptions?.find((opt) => opt.type === 'delivery')
    const dcBackedDelivery = item.deliveryDcStock ?? item.dcStock ?? 0
    return (
      (deliveryOption?.stockQuantity ?? 0) >= item.quantity ||
      dcBackedDelivery >= item.quantity
    )
  })
  const deliveryEnabled =
    currentMethod === 'delivery' || (groupSupportsDelivery && everyItemHasDeliveryStock)

  // AC5 — Delivery branch is system-determined; may differ from the
  // Collection branch. Take the first item that carries a
  // cross-branch source annotation, else fall back to the group branch.
  //
  // Items whose delivery is on backorder are skipped: their stored
  // `deliverySourceBranchName` was captured by an earlier stock probe
  // and doesn't describe the backorder — the supplier fulfils the
  // backorder from the group's own branch (or wherever it chooses at
  // the promised date). Change Item resolves the same way via its
  // live re-probe, which returns a null source for backorder delivery
  // and then falls through to the branch name.
  //
  // Predicate mirrors Cart.tsx:549-553 — a line is on backorder for
  // delivery when the requested qty exceeds delivery capacity. Do NOT
  // infer from date equality: for API-date suppliers (Wolseley),
  // `backorderDeliveryDate` shares the same earliest_delivery value
  // used for a normal in-stock delivery, so equal dates are not proof
  // of a backorder.
  const isBackorderDelivery = (item: CartItem) => {
    if (item.backorderAllowed !== true) return false
    if (item.backorderDeliveryDate == null) return false
    const deliveryOption = item.fulfillmentOptions?.find((opt) => opt.type === 'delivery')
    const dcBackedDelivery = item.deliveryDcStock ?? item.dcStock ?? 0
    const deliveryCapacity = Math.max(deliveryOption?.stockQuantity ?? 0, dcBackedDelivery)
    return item.quantity > deliveryCapacity
  }
  const deliverySourceBranchName =
    branchGroup.items.find(
      (item) => !isBackorderDelivery(item) && item.deliverySourceBranchName,
    )?.deliverySourceBranchName ?? branchGroup.branchName

  // Header dates — MAX across the group's items (same rule as BM-013).
  const collectionDates = branchGroup.items
    .map((item) => item.pickupOrderDate)
    .filter((d): d is string => !!d)
  const collectionMaxDate = collectionDates.length
    ? collectionDates.reduce((max, d) => (d > max ? d : max))
    : null
  const collectionDateLabel = formatEarliestDate(collectionMaxDate)

  const deliveryDates = branchGroup.items
    .map((item) => item.deliveryOrderDate)
    .filter((d): d is string => !!d)
  const deliveryMaxDate = deliveryDates.length
    ? deliveryDates.reduce((max, d) => (d > max ? d : max))
    : null
  const deliveryDateLabel = formatEarliestDate(deliveryMaxDate)

  // AC4 — Alternative branches must have stock for EVERY item. Same
  // endpoint ProductDetail's "Change branch" picker uses: call
  // getLiveStock with each item's raw productId — the endpoint expands
  // to the nearest branches that carry variants of that master and
  // returns per-branch stock. Intersect across items so a branch is
  // only eligible when every line at that supplier has stock there.
  const itemProductIds = branchGroup.items.map((item) => String(item.productId))
  const itemIdsKey = [...itemProductIds].sort().join(',')
  const { data: alternatives = [], isLoading: loadingAlternatives } = useQuery<Alternative[]>({
    queryKey: [
      'manage-order-alternatives',
      String(branchGroup.supplierId),
      String(branchGroup.branchId ?? ''),
      itemIdsKey,
      postcode ?? '',
    ],
    queryFn: async () => {
      if (!postcode) return []
      const currentSupplierIdStr = String(branchGroup.supplierId)
      const currentBranchIdOnlyStr = branchGroup.branchId != null ? String(branchGroup.branchId) : null

      // Scope every per-item call to the group's supplier so the
      // response mirrors ProductDetail's per-supplier stock contract
      // (same params PD uses: raw product id + postcode + supplier id).
      // Without the supplier id the endpoint still works but skipping
      // the scope leaves us at the mercy of the input's implicit
      // supplier lookup — a small correctness risk that has no
      // downside to close.
      const supplierIdNumeric = Number(branchGroup.supplierId)
      const supplierIdParam = Number.isFinite(supplierIdNumeric) ? supplierIdNumeric : undefined

      // Discover per-item candidate branches with the endpoint's default
      // qty=1 probe. Store raw shelf/DC bits (not a pre-computed earliest)
      // so the final display date can pick up the qty-aware DC date from
      // the follow-up getCartStock call below.
      const perItemBranches = await Promise.all(
        branchGroup.items.map(async (item) => {
          const map = new Map<
            string,
            {
              branchName: string
              distanceMiles: number | null
              branchCollection: number
              branchEarliest: string | null
              dcEarliest: string | null
              // Set when shelf + DC don't cover the qty but backorder is
              // allowed for this (supplier, product) — the branch stays in
              // the intersection and shows the supplier's backorder date.
              backorderDate: string | null
            }
          >()
          try {
            const resp = await api.getLiveStock(
              [item.productId],
              postcode,
              latitude,
              longitude,
              supplierIdParam,
            )
            for (const entry of resp?.stock ?? []) {
              const entrySupplierId = (entry as { supplier_id?: string | number }).supplier_id
              // Not all endpoints stamp supplier_id — treat missing as
              // "matches" (the endpoint scoped the response to this raw
              // productId's supplier by definition).
              if (entrySupplierId != null && String(entrySupplierId) !== currentSupplierIdStr) continue
              if (entry.branch_id == null) continue
              const branchIdStr = String(entry.branch_id)
              if (branchIdStr === currentBranchIdOnlyStr) continue
              const canCollect = (entry as { can_collect?: boolean }).can_collect
              if (canCollect === false) continue
              // DC-backed pickup counts. Take max(branch, DC) so a branch
              // whose today shelf is empty but whose warehouse can cover the
              // qty still qualifies — same rule ChangeItem / ProductDetail /
              // Cart use. Without this fallback, a CPS line like 954320 at
              // PORTSWOOD (0 today, 7857 later via DC) knocks PORTSWOOD out
              // of the intersection and disables Pickup for the whole group.
              const branchCollection =
                (entry as { collection_stock?: number }).collection_stock
                ?? entry.stock_quantity
                ?? entry.branch_stock
                ?? 0
              const dcBackedPickup =
                (entry as { pickup_dc_stock?: number }).pickup_dc_stock
                ?? (entry as { dc_stock?: number }).dc_stock
                ?? 0
              const collectStock = Math.max(branchCollection, dcBackedPickup)
              const backorderAllowed =
                (entry as { backorder_allowed?: boolean }).backorder_allowed === true
              const backorderCollectionDate =
                (entry as { backorder_collection_date?: string | null }).backorder_collection_date ?? null
              // Shelf + DC must cover the qty — otherwise the branch only
              // qualifies via backorder (allowed AND supplier gave a date).
              // The branch PATCH doesn't validate against qty, so an item
              // moved to a branch that can't cover it must be either fully
              // stocked or on backorder.
              const canCoverByStock = collectStock >= item.quantity
              const canCoverByBackorder =
                backorderAllowed && backorderCollectionDate != null
              if (!canCoverByStock && !canCoverByBackorder) continue
              const branchName = (entry as { branch_name?: string }).branch_name ?? ''
              const distanceMiles = (entry as { distance_miles?: number | null }).distance_miles ?? null
              const branchEarliest = (entry as { earliest_collection?: string | null }).earliest_collection ?? null
              const dcEarliest = (entry as { pickup_dc_date?: string | null }).pickup_dc_date ?? null
              map.set(branchIdStr, {
                branchName,
                distanceMiles,
                branchCollection,
                branchEarliest,
                dcEarliest,
                backorderDate: canCoverByStock ? null : backorderCollectionDate,
              })
            }
          } catch {
            // Per-item outage — leave this line's set empty so the
            // intersection returns nothing rather than falsely including
            // branches that may not carry it.
          }
          return map
        }),
      )

      if (perItemBranches.length === 0) return []

      // Intersect first — only branches present in every item's map qualify.
      const first = perItemBranches[0]
      const eligibleBranchIds: string[] = []
      for (const [branchId] of first) {
        if (perItemBranches.every((m) => m.has(branchId))) {
          eligibleBranchIds.push(branchId)
        }
      }

      if (eligibleBranchIds.length === 0) return []

      // Qty-aware re-probe (Codex P2). getLiveStock above ran at qty=1;
      // Wolseley/UKPS only populate pickup_dc_date when the probed qty
      // exceeds branch shelf, so a large-qty line whose alternative branch
      // has some shelf stock would show a stale/null DC date otherwise.
      // Fire ONE getCartStock with the real cart quantities for the
      // (product, branch) pairs that need DC coverage — backend forces a
      // live call for qty > 1 so it never collides with the cached qty-1
      // entry. Failure falls through to whatever the initial probe gave us.
      const cartStockProbes: Array<{
        raw_product_id: number | string
        branch_id: string
        quantity: number
      }> = []
      for (const branchId of eligibleBranchIds) {
        for (let itemIdx = 0; itemIdx < branchGroup.items.length; itemIdx++) {
          const item = branchGroup.items[itemIdx]
          const info = perItemBranches[itemIdx].get(branchId)
          if (!info) continue
          // Above the shelf cap → probe. Covers both the DC-fallback case
          // and the API-date backorder case (Wolseley re-quotes its
          // earliest date at the real qty).
          if (item.quantity <= info.branchCollection) continue
          cartStockProbes.push({
            raw_product_id: item.productId,
            branch_id: branchId,
            quantity: item.quantity,
          })
        }
      }

      if (cartStockProbes.length > 0) {
        try {
          const cartStockResp = await api.getCartStock(
            cartStockProbes,
            postcode,
            latitude,
            longitude,
          )
          for (const entry of cartStockResp?.stock ?? []) {
            const branchIdStr = String(entry.branch_id)
            const productIdStr = String(entry.raw_product_id)
            const itemIdx = branchGroup.items.findIndex(
              (candidate) => String(candidate.productId) === productIdStr,
            )
            if (itemIdx < 0) continue
            const info = perItemBranches[itemIdx].get(branchIdStr)
            if (!info) continue
            const dcDate = (entry as { pickup_dc_date?: string | null }).pickup_dc_date ?? null
            if (dcDate != null) info.dcEarliest = dcDate
          }
        } catch {
          // Qty-aware probe failed — final earliest computation below uses
          // whatever the qty=1 probe gave us. Missing DC date on a
          // DC-covered line will surface as null (row shows no date)
          // rather than a misleading branch "Today".
        }
      }

      // Build final alternatives with corrected dates. Group-level earliest
      // = MAX across items at this branch — "when the WHOLE group is ready
      // here". Date rule per item: branch date when the shelf alone covers
      // the qty; else the qty-aware DC date; else the backorder date (set
      // during the intersection when neither shelf nor DC covered the qty
      // and the supplier allows backorder). Unknown date beats a
      // misleading one — a null anywhere in the group blanks the row.
      const eligible: Alternative[] = []
      for (const branchId of eligibleBranchIds) {
        const firstInfo = first.get(branchId)
        if (!firstInfo) continue
        let groupEarliest: string | null = null
        let missingDate = false
        for (let itemIdx = 0; itemIdx < perItemBranches.length; itemIdx++) {
          const item = branchGroup.items[itemIdx]
          const itemInfo = perItemBranches[itemIdx].get(branchId)
          if (!itemInfo) {
            missingDate = true
            break
          }
          // Precedence when shelf alone can't cover the qty:
          // backorderDate (set only when the branch was admitted via
          // backorder-only) wins over dcEarliest. A backorder-admitted
          // branch has DC that could cover at most the qty=1 probe's
          // capacity — its dcEarliest describes a partial stocked chunk,
          // not the whole line, so the backorder date is the honest
          // "ready when" for this item.
          const itemEarliest =
            item.quantity <= itemInfo.branchCollection
              ? itemInfo.branchEarliest
              : (itemInfo.backorderDate ?? itemInfo.dcEarliest)
          if (itemEarliest == null) {
            missingDate = true
            break
          }
          groupEarliest =
            groupEarliest == null || itemEarliest > groupEarliest
              ? itemEarliest
              : groupEarliest
        }
        eligible.push({
          branchId,
          branchName: firstInfo.branchName,
          distanceMiles: firstInfo.distanceMiles,
          earliestCollection: missingDate ? null : groupEarliest,
        })
      }

      eligible.sort((a, b) => {
        if (a.distanceMiles == null) return 1
        if (b.distanceMiles == null) return -1
        return a.distanceMiles - b.distanceMiles
      })
      // Cap at the top 3 nearest to match the mock's list length.
      return eligible.slice(0, 3)
    },
    enabled: open && !!postcode,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // A "merged" delivery group is any delivery group whose branchId was
  // rewritten to the delivery source by Cart's groupCartByBranch — the
  // source (e.g. CASTLEFORD 189 mi away) is not a real pickup location
  // for the underlying lines. Detect this by comparing each item's own
  // branchId to the group's branchId; any mismatch means the group is
  // showing a borrowed source. Covers both cases:
  //   - single item / multi items from the SAME own branch borrowing
  //     from another source (the common cross-borrow shape),
  //   - multi items from DIFFERENT own branches that all borrow from
  //     the same source.
  // A "size > 1" count only caught the second case and let the first
  // slip through, leaving a scenario where Apply on Pickup would stamp
  // the source branch onto the line. Any pickup switch on a merged
  // group must be routed through a common alternative branch (the
  // alternatives query intersects per-item stock, so it only surfaces
  // branches that carry every line). The "current" (delivery source)
  // row must not be selectable for pickup, and Apply is gated on the
  // engineer having picked a real alternative.
  const isMergedDeliveryGroup =
    currentMethod === 'delivery' &&
    branchGroup.items.some(
      (item) => String(item.branchId ?? '') !== String(branchGroup.branchId ?? ''),
    )

  // Pickup is available if the engineer is already there (trivially),
  // OR every line has enough stock at the current branch, OR the
  // alternatives query returned at least one branch where every line
  // has enough pickup stock (the intersection guarantees "every line").
  // Previous version only checked the current branch's flags, so an
  // engineer on a delivery-only branch couldn't reach a valid pickup
  // alternative because the Pickup radio was permanently disabled.
  //
  // For merged delivery groups the per-item everyItemHasPickupStock
  // check is misleading (each item has stock at its OWN branch, but
  // those branches differ), so pickup only unlocks when alternatives
  // has at least one common branch stocking every line.
  const pickupEnabled =
    currentMethod === 'pickup' ||
    (isMergedDeliveryGroup
      ? alternatives.length > 0
      : (groupSupportsPickup && everyItemHasPickupStock) || alternatives.length > 0)

  const selectedIsCurrent = selectedBranchId === currentBranchIdStr
  const selectedAlt = alternatives.find((a) => a.branchId === selectedBranchId)
  const collectionBranchName = selectedIsCurrent
    ? branchGroup.branchName
    : selectedAlt?.branchName ?? branchGroup.branchName
  const collectionBranchDistance = selectedIsCurrent
    ? branchGroup.distanceMiles
    : selectedAlt?.distanceMiles ?? null
  // Date shown below the Pickup label — follows the selected branch so
  // switching in the picker updates the tile (matches Change Item's
  // top-of-sheet date). Current branch uses the header's MAX-across-items
  // pickup date; alternatives use the intersected group-level earliest
  // (already MAX-across-items in the alternatives query).
  const selectedPickupDateLabel = selectedIsCurrent
    ? collectionDateLabel
    : formatEarliestDate(selectedAlt?.earliestCollection ?? null)

  // For merged delivery groups the "current" branch is the delivery
  // source (e.g. CASTLEFORD, 189 mi) — never a valid pickup destination.
  // Apply must require an alternative pick; the current row is hidden
  // from the branch list; and the branch list opens automatically when
  // the engineer taps Pickup so they land straight on the choices.
  const requiresAlternativeForPickup =
    isMergedDeliveryGroup && method === 'pickup'
  const hasChanges = requiresAlternativeForPickup
    ? !selectedIsCurrent
    : method !== currentMethod || (method === 'pickup' && !selectedIsCurrent)

  useEffect(() => {
    if (requiresAlternativeForPickup) setShowBranchList(true)
  }, [requiresAlternativeForPickup])

  // For a merged delivery group, seed selectedBranchId to the nearest
  // common alternative once alternatives finish loading. Without this,
  // the Pickup card previews the delivery source (e.g. CASTLEFORD 189 mi)
  // — visually broken and useless as a "if you switched to Pickup, this
  // is where" hint. The guard `selectedBranchId === currentBranchIdStr`
  // ensures we only overwrite the untouched on-open seed, never a branch
  // the engineer has already picked. Non-merged groups are unaffected
  // (the isMergedDeliveryGroup gate is false for them).
  const firstAlternativeBranchId = alternatives[0]?.branchId ?? null
  useEffect(() => {
    if (!isMergedDeliveryGroup) return
    if (!firstAlternativeBranchId) return
    if (selectedBranchId !== currentBranchIdStr) return
    setSelectedBranchId(firstAlternativeBranchId)
  }, [isMergedDeliveryGroup, firstAlternativeBranchId, selectedBranchId, currentBranchIdStr])

  const productCount = branchGroup.items.length
  const productWord = `product${productCount !== 1 ? 's' : ''}`
  // AC7 — impact summary. Only rendered when there's something to summarise.
  const impactSummary = !hasChanges
    ? null
    : method === 'pickup'
      ? `All ${productCount} ${productWord} move to ${collectionBranchName} for collection`
      : `All ${productCount} ${productWord} move to delivery`

  const handleApply = async () => {
    if (!hasChanges || isApplying) return
    setIsApplying(true)
    try {
      await onApply({
        // For delivery, the branch is system-determined — parent uses
        // the group's own branch when applying.
        newBranchId: method === 'pickup' ? selectedBranchId : null,
        newMethod: method,
      })
      onClose()
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && !isApplying && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content
          className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl shadow-xl focus:outline-none"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="h-1 w-9 rounded-full bg-gray-300" />
          </div>

          {/* Header */}
          <div className="px-4 py-3 flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-base font-bold text-gray-900">
                Manage order
              </Dialog.Title>
              <Dialog.Description className="text-xs text-gray-500 mt-0.5">
                {branchGroup.supplierName} · {productCount} {productWord}
              </Dialog.Description>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-none w-8 h-8 grid place-items-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 pb-4 space-y-3">
            {/* Collection card (AC2, AC3, AC4) */}
            <div
              className={clsx(
                'rounded-xl border overflow-hidden transition-colors',
                !pickupEnabled
                  ? 'border-gray-100'
                  : method === 'pickup'
                    ? 'border-brand-primary'
                    : 'border-gray-200 hover:border-gray-300',
              )}
            >
              <button
                type="button"
                onClick={() => pickupEnabled && setMethod('pickup')}
                disabled={!pickupEnabled}
                className="w-full px-4 py-2.5 text-left disabled:cursor-not-allowed"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={clsx(
                      'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                      !pickupEnabled
                        ? 'border-gray-200 bg-gray-50'
                        : method === 'pickup'
                          ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                          : 'border-gray-300',
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={clsx(
                        'text-sm font-bold uppercase flex items-center gap-2',
                        pickupEnabled ? 'text-gray-900' : 'text-gray-400',
                      )}
                    >
                      Collection
                    </p>
                    <p
                      className={clsx(
                        'text-sm font-semibold mt-0.5',
                        pickupEnabled ? 'text-gray-900' : 'text-gray-400',
                      )}
                    >
                      {pickupEnabled
                        ? (selectedPickupDateLabel ?? 'Date not available')
                        : 'Not available for all items'}
                      {pickupEnabled && (
                        <span className="font-normal text-gray-500"> · All items</span>
                      )}
                    </p>
                  </div>
                </div>
              </button>
              {/* Selected branch row — hidden for a merged delivery group
                  when the selection is still the delivery source. Once
                  alternatives load, the auto-seed effect above swaps the
                  selection to the nearest common branch and the row shows
                  it (e.g. "PORTSWOOD · 3.3 mi"). Row stays hidden while
                  alternatives are loading and when none exist so we never
                  surface the misleading source-branch preview (e.g.
                  "CASTLEFORD · 189 mi" under Pickup). */}
              {!(isMergedDeliveryGroup && selectedIsCurrent) && (
                <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-2">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-800 min-w-0">
                    <MapPin className="h-3.5 w-3.5 flex-none text-gray-500" />
                    <span className="truncate">{collectionBranchName}</span>
                    {collectionBranchDistance != null && (
                      <span className="text-gray-500 flex-shrink-0"> · {collectionBranchDistance.toFixed(1)} mi</span>
                    )}
                  </p>
                  {method === 'pickup' && !showBranchList && (
                    <button
                      type="button"
                      onClick={() => setShowBranchList(true)}
                      className="text-xs font-semibold text-brand-primary hover:opacity-80 transition-opacity flex-shrink-0"
                    >
                      Change
                    </button>
                  )}
                </div>
              )}
              {/* Branch list — unified: current + alternatives sorted by
                  distance so the picker reads in the same order the
                  engineer sees on Change Item / Product Detail. The
                  current selection is marked with a tick; it is NOT
                  hoisted to the top. Current is omitted for a merged
                  delivery group (its "current" IS the delivery source
                  and isn't a valid pickup destination). */}
              {method === 'pickup' && showBranchList && (
                <div className="px-4 pb-3">
                  {loadingAlternatives ? (
                    <div className="text-xs text-gray-500 py-3">
                      Loading branches with stock…
                    </div>
                  ) : (() => {
                    const pickerBranches: Array<{
                      branchId: string
                      branchName: string
                      distanceMiles: number | null
                      earliestLabel: string | null
                    }> = []
                    if (!requiresAlternativeForPickup && currentBranchIdStr != null) {
                      pickerBranches.push({
                        branchId: currentBranchIdStr,
                        branchName: branchGroup.branchName,
                        distanceMiles: branchGroup.distanceMiles,
                        earliestLabel: collectionDateLabel,
                      })
                    }
                    for (const alt of alternatives) {
                      pickerBranches.push({
                        branchId: alt.branchId,
                        branchName: alt.branchName,
                        distanceMiles: alt.distanceMiles,
                        earliestLabel: formatEarliestDate(alt.earliestCollection),
                      })
                    }
                    // AC5: nearest first, capped, keeping the branch in use
                    // in the list even when a move has left it well down the
                    // order — so the engineer sees what they'd be leaving.
                    const visibleBranches = nearestBranchOptions(
                      pickerBranches,
                      currentBranchIdStr,
                    )
                    if (visibleBranches.length === 0) {
                      return (
                        <div className="rounded-lg border border-gray-200 px-3 py-2.5 text-xs text-gray-500">
                          No branches carry stock for all items in this order.
                        </div>
                      )
                    }
                    return (
                      <>
                        <div className="rounded-lg border border-gray-200 overflow-hidden">
                          {visibleBranches.map((branch, idx) => {
                            const isSelected = branch.branchId === selectedBranchId
                            return (
                              <button
                                key={branch.branchId}
                                type="button"
                                onClick={() => {
                                  setSelectedBranchId(branch.branchId)
                                  setShowBranchList(false)
                                }}
                                className={clsx(
                                  'w-full px-3 py-2.5 text-left flex items-center justify-between transition-colors hover:bg-gray-50',
                                  idx > 0 && 'border-t border-gray-100',
                                  isSelected && 'bg-gray-50',
                                )}
                              >
                                <div>
                                  <p className="text-sm text-gray-900">{branch.branchName}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {branch.distanceMiles != null
                                      ? `${branch.distanceMiles.toFixed(1)} mi away`
                                      : 'Current branch'}
                                    {branch.earliestLabel && ` · ${branch.earliestLabel}`}
                                  </p>
                                </div>
                                {isSelected && <Check className="w-4 h-4 text-brand-primary" />}
                              </button>
                            )
                          })}
                        </div>
                        {!requiresAlternativeForPickup && alternatives.length === 0 && (
                          <p className="mt-2 text-xs text-gray-500">
                            No other branches carry stock for all items in this order.
                          </p>
                        )}
                      </>
                    )
                  })()}
                </div>
              )}
            </div>

            {/* Delivery card (AC2, AC5, AC6) — branch is read-only */}
            <button
              type="button"
              onClick={() => deliveryEnabled && setMethod('delivery')}
              disabled={!deliveryEnabled}
              className={clsx(
                'w-full rounded-xl border px-4 py-3 text-left transition-colors',
                !deliveryEnabled
                  ? 'border-gray-100 cursor-not-allowed'
                  : method === 'delivery'
                    ? 'border-brand-primary'
                    : 'border-gray-200 hover:border-gray-300',
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={clsx(
                    'mt-0.5 h-4 w-4 flex-none rounded-full border-2',
                    method === 'delivery' && deliveryEnabled
                      ? 'border-brand-primary bg-brand-primary shadow-[inset_0_0_0_3px_white]'
                      : 'border-gray-300',
                  )}
                />
                <div className="flex-1 min-w-0">
                  {deliveryEnabled ? (
                    <>
                      <p className="text-sm font-bold text-gray-900 uppercase flex items-center gap-2">
                        Delivery
                      </p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">
                        {deliveryDateLabel ?? 'Date not available'}
                        <span className="font-normal text-gray-500"> · All items</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        from {deliverySourceBranchName}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-bold text-gray-300 uppercase flex items-center gap-2">
                        Delivery
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Not available for delivery.</p>
                    </>
                  )}
                </div>
              </div>
            </button>

            {/* AC7 — impact summary */}
            {impactSummary && (
              <p className="text-xs text-gray-600 px-1 pt-1">{impactSummary}</p>
            )}
          </div>

          {/* Footer (AC8) */}
          <div className="sticky bottom-0 px-4 py-3 border-t border-gray-100 bg-white">
            <Button
              onClick={handleApply}
              disabled={!hasChanges || isApplying}
              variant="primary"
              className="w-full"
            >
              {isApplying ? 'Updating…' : `Update ${productCount} ${productWord}`}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
